import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BackToDashboard from '../components/BackToDashboard';
import { adminListings, adminOrgs } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { config } from '../config';
import type { Listing } from '../api/client';
import { formatPrice } from '../utils/format';

const PAGE_SIZE = 24;
const STATUS_OPTIONS = ['', 'draft', 'pending_review', 'published', 'unpublished'] as const;
type SortOption = 'newest' | 'oldest' | 'price_asc' | 'price_desc' | 'title';

function ExtendExpiryButton({ listingId, orgId, onDone }: { listingId: string; orgId: string; onDone: () => void }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const handleExtend = async () => {
    setLoading(true);
    try {
      await adminListings.extendExpiry(listingId, orgId, { extendByDays: 30 });
      onDone();
    } finally {
      setLoading(false);
    }
  };
  return (
    <button type="button" onClick={handleExtend} disabled={loading} className="dash-btn dash-btn--secondary dash-btn--sm">
      {loading ? '…' : t('listingsManager.extend1mo')}
    </button>
  );
}

function statusClass(s: string) {
  if (s === 'published') return 'dash-badge dash-badge--success';
  if (s === 'pending_review') return 'dash-badge dash-badge--warning';
  if (s === 'unpublished') return 'dash-badge dash-badge--danger';
  return 'dash-badge dash-badge--neutral';
}

export default function ListingsManager() {
  const { profile } = useAuth();
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState<Listing[]>([]);
  const [nextToken, setNextToken] = useState<string | undefined>();
  const [pageStack, setPageStack] = useState<Array<{ token: string; items: Listing[] }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [orgs, setOrgs] = useState<{ orgId: string; name: string }[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [actingId, setActingId] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterType, setFilterType] = useState<string>('');
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const isAdmin = profile?.role === 'admin';
  const effectiveOrgId = isAdmin ? selectedOrgId : profile?.orgId;

  const load = (token?: string) => {
    if (!effectiveOrgId) {
      setLoading(false);
      return;
    }
    if (!token) setPageStack([]);
    setLoading(true);
    setError(null);
    const params: { limit: string; nextToken?: string; type?: string; status?: string } = { limit: String(PAGE_SIZE) };
    if (token) params.nextToken = token;
    if (filterType) params.type = filterType;
    if (filterStatus) params.status = filterStatus;
    adminListings
      .listOrg(effectiveOrgId, params)
      .then((res) => {
        setItems(res.items);
        setNextToken(res.nextToken);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  const goToNextPage = () => {
    if (!nextToken || loading) return;
    setPageStack((prev) => [...prev, { token: nextToken, items: [...items] }]);
    load(nextToken);
  };

  const goToPreviousPage = () => {
    if (pageStack.length === 0 || loading) return;
    const stack = [...pageStack];
    const prev = stack.pop();
    if (!prev) return;
    setPageStack(stack);
    setItems(prev.items);
    setNextToken(prev.token);
  };

  const sortedItems = useMemo(() => {
    const list = [...items];
    if (sortBy === 'newest') list.sort((a, b) => (new Date(b.updatedAt ?? b.createdAt).getTime()) - (new Date(a.updatedAt ?? a.createdAt).getTime()));
    else if (sortBy === 'oldest') list.sort((a, b) => (new Date(a.updatedAt ?? a.createdAt).getTime()) - (new Date(b.updatedAt ?? b.createdAt).getTime()));
    else if (sortBy === 'price_asc') list.sort((a, b) => (a.price ?? 0) - (b.price ?? 0));
    else if (sortBy === 'price_desc') list.sort((a, b) => (b.price ?? 0) - (a.price ?? 0));
    else if (sortBy === 'title') list.sort((a, b) => (a.titleEn ?? '').localeCompare(b.titleEn ?? ''));
    return list;
  }, [items, sortBy]);

  useEffect(() => {
    if (isAdmin) {
      adminOrgs.list().then((r) => {
        setOrgs(r.items);
        if (r.items.length > 0 && !selectedOrgId) setSelectedOrgId(r.items[0].orgId);
      }).catch(() => setOrgs([]));
    }
  }, [isAdmin]);

  useEffect(() => {
    setPageStack([]);
    load();
  }, [effectiveOrgId, filterStatus, filterType]);

  const handleApprove = async (l: Listing) => {
    setActingId(l.listingId);
    try {
      await adminListings.approve(l.listingId, l.orgId);
      setItems((prev) => prev.map((i) => (i.listingId === l.listingId ? { ...i, status: 'published' } : i)));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setActingId(null);
    }
  };

  const handleUnpublish = async (l: Listing) => {
    setActingId(l.listingId);
    try {
      await adminListings.unpublish(l.listingId, l.orgId);
      setItems((prev) => prev.map((i) => (i.listingId === l.listingId ? { ...i, status: 'unpublished' } : i)));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setActingId(null);
    }
  };

  const hasCognito = !!(config.cognitoUserPoolId && config.cognitoClientId);

  if (!hasCognito) {
    return (
      <div className="dash-page">
        <h1 className="dash-page-title">{t('listingsManager.listings')}</h1>
        <div className="dash-alert" style={{ background: '#fef3c7', borderColor: '#fcd34d', color: '#92400e' }}>
          <p style={{ margin: 0, marginBottom: '0.75rem' }}><strong>{t('listingsManager.cognitoNotConfigured')}</strong> {t('listingsManager.cognitoHelp')}</p>
          <p style={{ margin: 0, fontSize: '0.9rem', opacity: 0.9 }}>{t('listingsManager.cognitoLocalHelp')}</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="dash-page">
        <BackToDashboard />
        <p style={{ color: '#6b7280' }}>{t('listingsManager.loading')}</p>
      </div>
    );
  }

  return (
    <div className="dash-page">
      <BackToDashboard />
      <header className="dash-page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 className="dash-page-title">{isAdmin ? t('listingsManager.listingsByOrg') : t('listingsManager.myAds')}</h1>
          <p className="dash-page-desc">
            {isAdmin ? 'Manage listings by organization. Filter by status and type, sort by date or price.' : 'Your ads. Filter and sort to find listings quickly.'}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          {isAdmin && orgs.length > 0 && (
            <select
              value={selectedOrgId}
              onChange={(e) => setSelectedOrgId(e.target.value)}
              className="dash-form-select"
              style={{ width: 'auto', minWidth: 160 }}
            >
              {orgs.map((o) => (
                <option key={o.orgId} value={o.orgId}>{o.name || o.orgId}</option>
              ))}
            </select>
          )}
          <Link to={effectiveOrgId ? `/dashboard/listings/new?orgId=${effectiveOrgId}` : '/dashboard/listings/new'} className="dash-btn dash-btn--primary" style={{ textDecoration: 'none' }}>
            + {t('listingsManager.addProperty')}
          </Link>
        </div>
      </header>

      {error && <div className="dash-alert dash-alert--error">{error}</div>}

      {!effectiveOrgId && isAdmin ? (
        <div className="dash-empty">Select an organization to view listings.</div>
      ) : loading && items.length === 0 ? (
        <p style={{ color: '#6b7280' }}>{t('listingsManager.loading')}</p>
      ) : (
        <section className="dash-card">
          <div className="dash-card-body">
            <div className="dash-toolbar">
              <span className="dash-toolbar-label">{t('common.status')}</span>
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="dash-form-select">
                <option value="">{t('common.all')}</option>
                {STATUS_OPTIONS.filter(Boolean).map((s) => (
                  <option key={s} value={s}>{s === 'pending_review' ? t('listingsManager.pendingReview') : s}</option>
                ))}
              </select>
              <span className="dash-toolbar-label">{t('common.type')}</span>
              <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="dash-form-select">
                <option value="">{t('common.all')}</option>
                <option value="rent">{t('common.rent')}</option>
                <option value="sale">{t('common.buy')}</option>
                <option value="short_term">{t('common.shortTerm')}</option>
              </select>
              <span className="dash-toolbar-label">{t('common.sortBy')}</span>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value as SortOption)} className="dash-form-select">
                <option value="newest">{t('common.sortNewest')}</option>
                <option value="oldest">Oldest first</option>
                <option value="price_asc">{t('common.sortPriceAsc')}</option>
                <option value="price_desc">{t('common.sortPriceDesc')}</option>
                <option value="title">{t('listingsManager.title')}</option>
              </select>
            </div>

            {sortedItems.length === 0 ? (
              <div className="dash-empty">
                <p style={{ margin: 0 }}>{t('listingsManager.noListingsYet')}</p>
                <Link to="/dashboard/listings/new" className="dash-btn dash-btn--primary" style={{ marginTop: '1rem', textDecoration: 'none' }}>{t('listingsManager.addFirstProperty')}</Link>
              </div>
            ) : (
              <>
                <p className="dash-pagination-info" style={{ marginBottom: '0.75rem', textAlign: 'left' }}>
                  {sortedItems.length} {t('common.listingsCount', { count: sortedItems.length })}
                </p>
                <div className="dash-table-wrap">
                  <table className="dash-table">
                    <thead>
                      <tr>
                        <th style={{ width: 52 }}>{t('listingsManager.photos')}</th>
                        <th>{t('listingsManager.title')}</th>
                        <th>{t('listingsManager.type')}</th>
                        <th>{t('listingsManager.status')}</th>
                        <th style={{ textAlign: 'right' }}>{t('listingsManager.price')}</th>
                        <th>{t('listingsManager.views')}</th>
                        <th>{t('listingsManager.expires')}</th>
                        <th style={{ textAlign: 'right' }}>{t('listingsManager.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedItems.map((l) => (
                        <tr key={l.listingId}>
                          <td>
                            {l.thumbnailUrl ? (
                              <img src={l.thumbnailUrl} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 6 }} />
                            ) : (
                              <span style={{ fontSize: '0.8rem', color: '#9ca3af' }}>{(l.mediaKeys?.length ?? 0)}</span>
                            )}
                          </td>
                          <td style={{ fontWeight: 500 }}>{l.titleEn}</td>
                          <td style={{ textTransform: 'capitalize' }}>{l.type}</td>
                          <td><span className={statusClass(l.status)}>{l.status === 'pending_review' ? t('listingsManager.pendingReview') : l.status}</span></td>
                          <td style={{ textAlign: 'right' }}>{formatPrice(l.price, l.currency)}</td>
                          <td>{(l.viewCount ?? 0) > 0 ? t('common.views', { count: l.viewCount ?? 0 }) : '—'}</td>
                          <td style={{ fontSize: '0.8125rem', color: '#6b7280' }}>{l.expiresAt ? new Date(l.expiresAt).toLocaleDateString(i18n.language === 'ar' ? 'ar-EG' : 'en-CA') : '—'}</td>
                          <td style={{ textAlign: 'right' }}>
                            <div className="dash-actions-inline" style={{ justifyContent: 'flex-end' }}>
                              <Link to={`/listings/${l.listingId}`} style={{ color: 'var(--color-primary)', fontSize: '0.875rem' }}>{t('listingsManager.view')}</Link>
                              {isAdmin && l.status === 'pending_review' && (
                                <button type="button" disabled={actingId === l.listingId} onClick={() => handleApprove(l)} className="dash-btn dash-btn--primary dash-btn--sm">{actingId === l.listingId ? '…' : t('listingsManager.approve')}</button>
                              )}
                              {isAdmin && l.status === 'published' && (
                                <button type="button" disabled={actingId === l.listingId} onClick={() => handleUnpublish(l)} className="dash-btn dash-btn--danger dash-btn--sm">{actingId === l.listingId ? '…' : t('listingsManager.unpublish')}</button>
                              )}
                              <Link to={`/dashboard/listings/edit/${l.listingId}?orgId=${l.orgId}`} style={{ color: '#2563eb', fontSize: '0.875rem' }}>{t('listingsManager.edit')}</Link>
                              {isAdmin && l.expiresAt && <ExtendExpiryButton listingId={l.listingId} orgId={l.orgId} onDone={() => load()} />}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <nav role="navigation" aria-label="Pagination" className="dash-pagination">
                  <button type="button" onClick={goToPreviousPage} disabled={loading || pageStack.length === 0} className="dash-btn dash-btn--secondary dash-btn--sm">← {t('common.previousPage')}</button>
                  <span className="dash-pagination-info">{t('common.pageN', { n: pageStack.length + 1 })}</span>
                  <button type="button" onClick={goToNextPage} disabled={loading || !nextToken} className="dash-btn dash-btn--secondary dash-btn--sm">{loading ? t('common.loading') : `${t('common.nextPage')} →`}</button>
                </nav>
              </>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
